import { forwardRef, ReactElement, useEffect } from "react";
import { Box } from "./Box";
import MessageStore from "../stores/AlertMessageStore";
import { observer } from "mobx-react";
import { IconProps } from "./Icon";
import { IconButton } from "./Button";
import { useFloating } from "@floating-ui/react-dom";
import { classNames } from "../utils/classNames";
import { renderIcon } from "./iconHelpers";
import { isEscape } from "../utils/keyboardEvents";
import { isTMessage, TMessage } from "../i18n/tt";
import { isReactElement } from "../utils/reactHelpers";
import { DCCallback, useTranslation } from "../stores/RootStoreContext";
import styles from "./Alert.module.css";
import { FloatingOverlay } from "./FloatingOverlay";
import { Transition } from "@headlessui/react";
import { isFunction } from "lodash";
import { BoxProps } from "./boxHelpers";
import { Markdown } from "../text/Markdown";
import { IconName } from "./iconMapping";

export type AlertProps = BoxProps & {
  message: ReactElement | string | number | TMessage | (() => ReactElement);
  type: "info" | "warning" | "error" | "success";
  icon?: boolean | ReactElement | IconProps["name"] | IconProps;
  onClose?: DCCallback;
};

const iconMapping: Record<AlertProps["type"], IconName> = {
  info: "info",
  warning: "warning",
  success: "check-circle",
  error: "warning",
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { message, type, className, icon, onClose, ...props },
  ref,
) {
  const { t } = useTranslation();
  return (
    <Box
      ref={ref}
      alignItems={"center"}
      rounded
      className={classNames(className, styles.alert, `bg-${type} color-${type}`)}
      paddingLeft={"md"}
      gap={false}
      {...props}
    >
      {icon && <Box paddingY={"sm"}>{renderIcon(icon === true ? iconMapping[type] : icon)}</Box>}
      <Box grow padding={"sm"}>
        {isReactElement(message) ? (
          message
        ) : isTMessage(message) ? (
          <Markdown value={t(message)} inheritLinkColor />
        ) : isFunction(message) ? (
          message()
        ) : (
          message
        )}
      </Box>
      {onClose && (
        <div className={"tw-pr-1"}>
          <IconButton color={"inherit"} icon={"x"} tooltip={t("close")} onClick={onClose} />
        </div>
      )}
    </Box>
  );
});

export const AlertContainer = observer(function AlertContainer({
  messageStore,
}: {
  messageStore: MessageStore;
}) {
  const message = messageStore.message;
  const { refs, elements } = useFloating({ open: messageStore.messageVisible });

  useEffect(() => {
    const floatingEl = elements.floating;
    if (floatingEl) {
      const keyDownListener = (event: KeyboardEvent) => {
        if (isEscape(event)) {
          messageStore.hideMessage();
        }
      };
      const clickListener = (event: MouseEvent) => {
        if (
          event.target &&
          event.target !== floatingEl &&
          !floatingEl.contains(event.target as HTMLElement)
        ) {
          messageStore.hideMessage();
        }
      };
      document.body.addEventListener("keydown", keyDownListener, true);
      document.body.addEventListener("click", clickListener, true);
      return () => {
        document.body.removeEventListener("keydown", keyDownListener, true);
        document.body.removeEventListener("click", clickListener, true);
      };
    }
  }, [elements.floating, messageStore]);

  if (message) {
    return (
      <FloatingOverlay layer={"popover"} className={"tw-pointer-events-none"}>
        <div className={"tw-fixed tw-inset-0 tw-top-12"}>
          <Transition
            show={messageStore.messageVisible}
            enterFrom={"-tw-translate-y-full tw-opacity-0"}
            enterTo={"tw-translate-y-0 tw-opacity-100"}
            leaveFrom={"tw-opacity-100"}
            leaveTo={"tw-opacity-0"}
            className={"tw-transition tw-duration-200 tw-ease-in-out"}
          >
            <div className={"tw-flex tw-justify-center tw-mx-4 tw-pointer-events-auto"}>
              <Alert
                ref={refs.setFloating}
                message={message}
                type={messageStore.type}
                dropShadow={"lg"}
                maxWidth={"prose"}
                grow
                onClose={messageStore.hideMessage}
              />
            </div>
          </Transition>
        </div>
      </FloatingOverlay>
    );
  }
  return null;
});
